import { COMPONENT_FORM_CONFIGS } from '../componentFormConfigs';
import { useComponentForm, type ComponentFormApi } from '../useComponentForm';
import { cmsList, FormSubtab, handleTabToNext, type OptionRow } from '../formKit';
import { formatDecimalDisplay, formatIntegerDisplay } from '../formUtils';
import {
  BlankOffSlot,
  FormHeading,
  MBareInput,
  MBareSelect,
  PricingRows,
  PSelect,
  SBareInput,
} from './formCommon';
import type { ComponentFormProps } from './ComponentFormHost';

/**
 * Phase 6e2a — GAS HEATER form, re-cut against components/gas_heater/tmpl/
 * form.tmpl (verify round 1): hidden heading (`rating-mode hide`, Rating
 * radio only — no smartlist), subtab census properties/performance/pricing
 * (bypass commented out in the tmpl; NO remarks), all params selects
 * non-clickable, paired rows (Qty Wide x High, Face Velocity | APD, Input/
 * Output Rating triples, EADB | LADB).
 *
 *  - §49.2 guard: ONLY `rate` batches auto-save (engine-owned);
 *  - [NEG]#115 NOT PORTED: beforeRatingModeSelection references an
 *    unimported RatedGasHeaterModel — rating entry stays the chassis stub.
 */
const config = COMPONENT_FORM_CONFIGS.gas_heater;

/** the `get_one` handlebars helper (ahu/libs/handlebars/helpers.js:150-152) —
 *  the first truthy non-object argument, else ''. Falsy (0 included) falls
 *  through, exactly as on dev. */
const getOne = (...vals: unknown[]): unknown => vals.find((v) => v && typeof v !== 'object') ?? '';

/**
 * "Connection Hand" — the `.checkboxes-group` radio row at form.tmpl:101-122,
 * missing from the port entirely. SELECTION owns the value: legacy stamps
 * every radio `disabled tabindex="-1"`, so this row is display-only, and it
 * reads `gas_heater_params.conn_hand_id` first, falling back to the model's
 * own `conn_hand_id` (tmpl:107-111).
 */
const ConnHandRow = ({ api }: { api: ComponentFormApi }) => {
  const engine = api.catalogueFields.master_gas_heater_id;
  const param = engine?.paramValue('conn_hand_id');
  const current = String((param ?? api.model?.get('conn_hand_id')) ?? '');
  return (
    <li>
      <div>
        <div className="checkboxes-group">
          {cmsList(api.componentCms, 'connection_hand').map((r) => (
            <label className="radio" key={String(r.id)}>
              <input
                type="radio"
                className="gas_heater_params_field non-clickable"
                name="conn_hand_id"
                value={String(r.id)}
                checked={String(r.id) === current}
                disabled
                tabIndex={-1}
              />{' '}
              {String(r.name ?? '')}
            </label>
          ))}
        </div>{' '}
        <span className="label">Connection Hand</span>
      </div>
    </li>
  );
};

const GasHeaterForm = ({ row, section, section2, cms }: ComponentFormProps) => {
  const api = useComponentForm({
    config,
    row,
    section,
    section2,
    firstSubtab: 'properties',
  });
  const { model } = api;

  if (api.loading || !model) {
    return <div className="preloader dotted-preloader active" />;
  }
  const sub = (name: string) => !!api.subtabs[name];
  // withoutRatingSeparateAPI — catalogueParams parsed from the model's
  // gas_heater_params JSON (§50.1 gas_heater row).
  const ghParams = (() => {
    try {
      return JSON.parse(String(model.get('gas_heater_params') ?? '{}')) as Record<string, unknown>;
    } catch {
      return {} as Record<string, unknown>;
    }
  })();

  return (
    <>
      <FormHeading
        title="Gas Heater"
        api={api}
        wrapperClass="component-form-heading clearfix rating-mode hide"
        withRatingBlock
        ratingBlockDataName="gas_heater"
      />
      <form onKeyDown={handleTabToNext}>
        {/* id restores the legacy data-bar field lock (gas_heater form.tmpl:27):
            ahu/styles.css:4939-4973 keys `#gas-heater-form button` and
            `#gas-heater-form .sublist input|select|.selection|.rating-selection
            |.certification_output` on it. SELECTION owns these values. */}
        <ul className="inputs-group max-height-auto" id="gas-heater-form">
          <FormSubtab type="gas_heater" cid={row.id} name="properties" label="Properties" labelHidden open={sub('properties')} onToggle={api.toggleSubtab}>
            <PSelect api={api} target="master_gas_heater_id" name="mfg_id" label="Manufacturer" cmsPath="manufacturer" extraClass="non-clickable" noneMode="visible" />
            <PSelect api={api} target="master_gas_heater_id" name="series_id" label="Series" cmsPath="series" labelKey="series" extraClass="non-clickable" noneMode="visible" />
            <PSelect api={api} target="master_gas_heater_id" name="type_id" label="Type" cmsPath="type" extraClass="non-clickable" noneMode="visible" />
            <PSelect api={api} target="master_gas_heater_id" name="configuration" label="Configuration" cmsPath="configuration" labelKey="name" valueKey="name" extraClass="non-clickable" noneMode="visible" />
            {/* tmpl:90 — the row div is `multiply gas-heater-multiply`. The
                `multiply` half is what carries the pair grammar (styles.css
                :3990 `.multiply .multiply-sign` — width 1%, padding 0 4px,
                text-align center, text-transform uppercase); without it the
                sign keeps only the generic 12px/grey rule, so the boxes close
                up and the right one stops lining up with the rows above.
                `.gas-heater-multiply` alone buys nothing but focus colours —
                it is inside the `:not(...)` list of every generic div rule. */}
            <li>
              <div className="multiply gas-heater-multiply">
                {/* §JSX-WS — the tmpl newlines between these inline-blocks are
                    4px of real layout each (tmpl:91-93). */}
                <MBareInput api={api} name="qty_wide" formatter="integer" readOnly extraClass="medium-input" />{' '}
                <span className="multiply-sign">X</span>{' '}
                <MBareInput api={api} name="qty_high" formatter="integer" readOnly extraClass="medium-input" />{' '}
                <span className="label">
                  <span>Qty Wide</span> x <span>High</span>
                </span>
              </div>
            </li>
            <ConnHandRow api={api} />
            <PSelect api={api} target="master_gas_heater_id" name="heatex_mtl_id" label="Heatex Material" cmsPath="heatex_mtl" extraClass="non-clickable" noneMode="visible" />
            <PSelect api={api} target="master_gas_heater_id" name="tube_rows_vertically" label="Number of Tubes" cmsPath="tube_rows_vertically" labelKey="tube_rows_vertically" valueKey="tube_rows_vertically" extraClass="non-clickable" noneMode="visible" />
            <PSelect api={api} target="master_gas_heater_id" name="firing_mode_id" label="Firing Mode" cmsPath="firing_mode" extraClass="non-clickable" noneMode="visible" />
            <PSelect api={api} target="master_gas_heater_id" name="fuel_type_id" label="Fuel Type" cmsPath="fuel_type" extraClass="non-clickable" noneMode="visible" />
            <PSelect api={api} target="master_gas_heater_id" name="power_id" label="Voltage" cmsPath="power" extraClass="non-clickable" noneMode="visible" />
            <PSelect api={api} target="master_gas_heater_id" name="certification_id" label="Certification" cmsPath="certification" extraClass="non-clickable" noneMode="visible" />
          </FormSubtab>

          {/* tmpl:239 — ONLY the Properties label carries `class="hide"`.
              Performance's is a normal label, and it is the whole handle for
              the group: Bootstrap's `.hide{display:none!important}` (bundled
              by styles.css:2) took the toggle away, and since the subtab
              defaults closed there was then no way to reach the rows — the
              section read as missing rather than collapsed. */}
          <FormSubtab type="gas_heater" cid={row.id} name="performance" label="Performance" open={sub('performance')} onToggle={api.toggleSubtab}>
            {/* tmpl:241-259 — classless div, and the unit leads the row. */}
            <li>
              <div>
                <span className="unit">CFM</span>{' '}
                <MBareSelect
                  api={api}
                  name="air_stream_id"
                  options={
                    ((cms as Record<string, unknown> | undefined)?.air as
                      | { air_stream?: OptionRow[] }
                      | undefined)?.air_stream ?? []
                  }
                  extraClass="medium-input non-clickable"
                  noneMode="visible"
                />{' '}
                <MBareInput api={api} name="airflow_actual" formatter="integer" readOnly extraClass="medium-input performance-small non-clickable" fieldClass="model_field" />{' '}
                <span className="label">Air Stream &amp; Flow</span>
              </div>
            </li>
            {/* tmpl:275-289 — APD is `performance-small`, and the caption is
                one plain string, not two spans. */}
            <li>
              <div>
                <span className="unit">fpm</span>{' '}
                <MBareInput api={api} name="face_velocity_actual" readOnly extraClass="medium-input non-clickable" />{' '}
                <span className="unit long-unit">in WC</span>{' '}
                <MBareInput api={api} name="apd" readOnly extraClass="medium-input performance-small non-clickable" />{' '}
                <span className="label">Face Velocity | APD</span>
              </div>
            </li>
            <li>
              <div className="multiply-total multiply">
                <span className="unit">btu/hr</span>{' '}
                <input type="text" data-formatter_type="integer" autoComplete="off" name="input_rating_std" value={formatIntegerDisplay(ghParams.input_rating_std)} className="first-input small-input small" readOnly tabIndex={-1} onChange={() => undefined} />{' '}
                <span className="multiply-sign">X</span>{' '}
                <MBareInput api={api} name="qty" formatter="decimal" readOnly extraClass="small-input small" />{' '}
                <MBareInput api={api} name="input_rating_std_total" formatter="integer" readOnly extraClass="small-input small total-output" />{' '}
                <span className="label">Input Rating Std</span>
              </div>
            </li>
            <li>
              <div className="multiply-total multiply">
                <span className="unit">btu/hr</span>{' '}
                <MBareInput api={api} name="output_rating_actual" formatter="integer" readOnly extraClass="first-input small-input small" />{' '}
                <span className="multiply-sign">X</span>{' '}
                <MBareInput api={api} name="qty" formatter="decimal" readOnly extraClass="small-input small" />{' '}
                <MBareInput api={api} name="output_rating_actual_total" formatter="integer" readOnly extraClass="small-input small total-output" />{' '}
                <span className="label">Output Rating Actual</span>
              </div>
            </li>
            {/* tmpl:319-329 — classless div, so the unit sits in the gutter. */}
            <li>
              <div>
                <span className="unit">%</span>{' '}
                <input type="text" data-formatter_type="decimal" autoComplete="off" name="efficiency" value={formatDecimalDisplay(ghParams.efficiency)} className="model_field" readOnly tabIndex={-1} onChange={() => undefined} />{' '}
                <span className="label">Efficiency</span>
              </div>
            </li>
            {/* tmpl:335 — `{{get_one section.eadb_temp model.eadb_temp}}`, and
                the fallback is load-bearing rather than defensive: a gas heater
                "only ever operates in winter", so its save syncs the entering
                temp into the section's WINTER pair and never touches the
                unsuffixed (summer) `section.eadb_temp`
                (models_component_gas_heater.py:1375-1379). The port read the
                section field alone, so the row was blank on every gas heater —
                most visibly right after a Select, which is when the heater
                first acquires an eadb_temp of its own to show. */}
            <li>
              <div className="total-airflow">
                <span className="unit temp"> F </span>{' '}
                <SBareInput api={api} name="eadb_temp" value={getOne(section.eadb_temp, model.get('eadb_temp'))} readOnly max={100} />{' '}
                <input type="text" data-formatter_type="decimal" autoComplete="off" name="ladb_temp" className="model_field medium-input non-clickable" value={formatDecimalDisplay(model.get('ladb_temp') ?? model.get('ladb_temp_desired'))} readOnly tabIndex={-1} onChange={() => undefined} />{' '}
                <span className="label">
                  <span> EADB</span> | <span>LADB</span>
                </span>
              </div>
            </li>
          </FormSubtab>

          <FormSubtab type="gas_heater" cid={row.id} name="pricing" label="Pricing" open={sub('pricing')} onToggle={api.toggleSubtab}>
            <PricingRows api={api} label="Gas Heater" />
          </FormSubtab>

          <BlankOffSlot api={api} sectionId={section.id} />
        </ul>
      </form>
      <RequiredFieldNoteStub />
    </>
  );
};

/** Gas heater tmpl carries the same required-field footer note. */
const RequiredFieldNoteStub = () => (
  <p className="note">
    <i>*</i> Required Field...
  </p>
);

export default GasHeaterForm;
